import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function SpringLeagueRegistrationPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    preferredDay: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d66f87s2hrrhr32vf0h0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          experience: '',
          preferredDay: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://static.readdy.ai/image/538ca75b02212864d9ecfdf82df77bb0/cdeb6c781bca524449185652bdf81387.png" 
              alt="Golf simulator background" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Link 
              to="/winter-league" 
              className="inline-flex items-center text-green-200 hover:text-white mb-6 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to League Standings
            </Link>
            
            <div className="max-w-3xl">
              <span className="inline-block bg-yellow-500 text-gray-900 px-4 py-1 rounded-full text-sm font-bold mb-4">
                REGISTRATION OPEN - NOW UNTIL FILLED!
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Spring League 2025
              </h1>
              <p className="text-xl text-green-100 mb-2">
                April 6 - May 15 | Mobile Simulator Golf | $250 per golfer
              </p>
              <p className="text-green-200">
                Join our exciting Spring League season! Limited spots available for competitive and casual golfers alike.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              
              {/* League Info */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">League Details</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                        <i className="ri-calendar-event-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Season Duration</h3>
                        <p className="text-gray-600 text-sm">6 Week Season</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                        <i className="ri-map-pin-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                        <p className="text-gray-600 text-sm">Rivertown Amenities - Lodge</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                        <i className="ri-trophy-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Format - 8 Teams (2v2 Scramble - 9 Holes)</h3>
                        <p className="text-gray-600 text-sm">2-man scrambles uses each golfers shot but plays best position.&nbsp;</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                        <i className="ri-group-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Skill Level</h3>
                        <p className="text-gray-600 text-sm">All skill levels welcome - competitive for Casual, Intermediate, and Skilled levels</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-error-warning-line text-yellow-600 text-xl mr-3 mt-0.5"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Limited Spots Available!</h3>
                      <p className="text-gray-600 text-sm">
                        Register early to secure your spot. We'll contact you within 24-48 hours to confirm your registration and provide payment details.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-green-800 rounded-xl text-white">
                  <h3 className="font-semibold mb-3">Questions?</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <i className="ri-phone-line mr-2"></i>
                      <span>904-657-4212</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-2"></i>
                      <span>greatgolfonthego@gmail.com</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Registration Form */}
              <div>
                <form 
                  id="spring-league-form" 
                  data-readdy-form 
                  onSubmit={handleSubmit} 
                  className="bg-white rounded-xl p-8 shadow-lg border border-gray-100"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Register Now</h2>
                  <p className="text-gray-600 text-sm mb-6">Fill out the form below and we'll get back to you shortly.</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Golf Experience *</label>
                      <div className="relative">
                        <select
                          name="experience"
                          value={formData.experience}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none cursor-pointer"
                        >
                          <option value="">Select your experience level</option>
                          <option value="beginner">Beginner - New to golf</option>
                          <option value="casual">Casual - Play occasionally</option>
                          <option value="intermediate">Intermediate - Play regularly</option>
                          <option value="advanced">Advanced - Competitive player</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Day *</label>
                      <div className="relative">
                        <select
                          name="preferredDay"
                          value={formData.preferredDay}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none cursor-pointer"
                        >
                          <option value="">Select preferred day</option>
                          <option value="monday-evening">Monday Evening</option>
                          <option value="friday-evening">Friday Evening</option>
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                        placeholder="Any questions or special requests? Let us know..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                    </div>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-green-600 mr-2"></i>
                        <p className="text-green-800 text-sm">Thank you for registering! We'll contact you within 24-48 hours to confirm your spot.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-600 mr-2"></i>
                        <p className="text-red-800 text-sm">Sorry, there was an error. Please try again or contact us directly.</p>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full mt-6"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ri-golf-ball-line mr-2"></i>
                        Submit Registration
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
